import React, { useCallback, useEffect, useRef, useState } from 'react';
import Header from '../components/ui/Header';
import AuthenticationGuard from '../components/ui/AuthenticationGuard';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import sendChatCompletion from '../services/aiAssistantService';

const suggestions = [
  {
    title: 'Stay consistent with habits',
    description: "Ask the assistant for ideas to keep a streak or adjust your routine when you're stuck.",
    icon: 'Target',
  },
  {
    title: 'Make better flashcards',
    description: 'Let the assistant draft quick Q&A pairs or improve wording for spaced repetition.',
    icon: 'Brain',
  },
  {
    title: 'Summarize what you learned',
    description: 'Share a note or article and get a short summary or quiz questions.',
    icon: 'BookOpen',
  },
];

const quickPrompts = [
  'Help me design a 5-minute morning routine.',
  'Suggest flashcards for the last chapter I read.',
  'Give me a motivational reminder for tomorrow.',
];

const initialAssistantMessage = "Hi there! I'm your Cogito learning assistant. Ask me for study strategies, flashcard ideas, or quick motivation.";

const AIAssistantPage = () => {
  const [messages, setMessages] = useState([{ role: 'assistant', content: initialAssistantMessage }]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef(messages);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const sendMessage = useCallback(
    async (rawText) => {
      if (isLoading) {
        return;
      }

      const trimmed = rawText.trim();
      if (!trimmed) {
        return;
      }

      const userMessage = { role: 'user', content: trimmed };
      const nextMessages = [...messagesRef.current, userMessage];
      messagesRef.current = nextMessages;
      setMessages(nextMessages);
      setInputValue('');
      setError(null);
      setIsLoading(true);

      try {
        const assistantContent = await sendChatCompletion(nextMessages);
        const assistantMessage = { role: 'assistant', content: assistantContent };
        setMessages((prev) => {
          const updated = [...prev, assistantMessage];
          messagesRef.current = updated;
          return updated;
        });
      } catch (err) {
        setError(err?.message || 'The assistant is unavailable right now. Please try again soon.');
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading]
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      sendMessage(inputValue);
    },
    [inputValue, sendMessage]
  );

  const handlePromptClick = useCallback(
    (prompt) => {
      sendMessage(prompt);
    },
    [sendMessage]
  );

  return (
    <AuthenticationGuard isAuthenticated={true}>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8 space-y-8">
            <section className="text-center space-y-3">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium">
                <Icon name="Stars" size={16} />
                <span>AI Assistant</span>
              </div>
              <h1 className="text-3xl font-bold text-foreground">Your personal learning co-pilot</h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Start a conversation, tap a quick prompt, or ask for targeted advice. The assistant is ready to help you plan, review, and stay motivated.
              </p>
            </section>

            <section className="bg-card border border-border rounded-xl p-6 space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="MessageCircle" size={22} className="text-primary" />
                </div>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold text-foreground">Chat with the assistant</h2>
                  <p className="text-sm text-muted-foreground">
                    Ask questions about habits, flashcards, or learning strategy. The assistant responds with actionable guidance tailored to your goals.
                  </p>
                </div>
              </div>

              <div className="bg-background border border-border/60 rounded-xl p-4 space-y-4 max-h-[420px] overflow-y-auto">
                {messages.map((message, index) => {
                  const isUser = message.role === 'user';
                  return (
                    <div key={`${message.role}-${index}`} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                          isUser ? 'bg-primary text-white' : 'bg-muted text-foreground'
                        }`}
                      >
                        {message.content}
                      </div>
                    </div>
                  );
                })}
                {isLoading && (
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-primary"></span>
                    <span>Thinking...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {error && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <form className="flex flex-col sm:flex-row sm:items-end gap-3" onSubmit={handleSubmit}>
                <textarea
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder="Ask for study tips, flashcard ideas, or motivation..."
                  rows={3}
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  disabled={isLoading}
                />
                <Button type="submit" disabled={isLoading} iconName="Send">
                  {isLoading ? 'Sending...' : 'Send message'}
                </Button>
              </form>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {suggestions.map((item) => (
                  <div key={item.title} className="bg-muted/30 border border-border/60 rounded-lg p-4 space-y-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={item.icon} size={18} className="text-primary" />
                    </div>
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-muted/40 border border-dashed border-border rounded-xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-foreground">Quick prompts to try</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {quickPrompts.map((prompt) => (
                  <Button
                    key={prompt}
                    variant="ghost"
                    iconName="ArrowRight"
                    className="justify-start text-left"
                    onClick={() => handlePromptClick(prompt)}
                    disabled={isLoading}
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Tip: Tap a prompt to send it instantly, then refine the follow-up with your own context.
              </p>
            </section>

            <section className="bg-card border border-border rounded-xl p-6 space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Get the most out of AI assistance</h2>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Share your goal, context, and constraints for more helpful replies.</li>
                <li>• Ask the assistant to suggest follow-up questions or next steps.</li>
                <li>• Combine insights with your own reflection to keep learning personal.</li>
              </ul>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                <Button
                  variant="outline"
                  iconName="ExternalLink"
                  className="sm:w-auto w-full"
                  onClick={() => window.open('https://docs.cogito.app/ai-assistant-roadmap', '_blank')}
                >
                  See AI roadmap
                </Button>
                <Button
                  variant="ghost"
                  iconName="MessageCircle"
                  className="sm:w-auto w-full"
                  onClick={() => window.open('mailto:ideas@cogito.app', '_blank')}
                >
                  Share feedback
                </Button>
              </div>
            </section>
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default AIAssistantPage;
